import { Button } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./InvoiceDetailsNew.css";
import NewDiscountModal from "../modals/NewDiscountModal";
import Header from "../../../components/dms_common/Header";
import FormHeader from "../../../components/dms_common/FormHeader";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { getInvoicesColumns } from "../utils/utils";
import Search from "../../../components/dms_common/Search";
import { useSelector, useDispatch } from "react-redux";
import { claimSummarySkeleton } from "../utils/utils";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import {setSecondarySchemesRows} from "../../../stateManagement/actions/claims";
import CustomAccordian from "../../../components/dms_common/CustomAccordian";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { createScrollHandler, handleScroll, handleScrollCustomDataGrid, truncateToTwoDecimals } from "../../../components/dms_common/DmsUtils";

const InvoiceDetailsNew = () => {
  const [claimSummaryObject, setClaimSummaryObject] = useState(claimSummarySkeleton);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [isScrolling, setIsScrolling] = useState(false);

  const { 
    selectedSecondarySchemeRow, 
    month, 
    secondarySchemesRows,
    initialSecondarySchemesRows,
  } = useSelector((state: any) => state.claims);
  const initialFinalAmountsRef = useRef<Record<string, number>>({});
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {claimTypes, currency, caseUnitLabel, otherUnitLabel, packUnitLabel, secondarySchemesUIConfig, batchIdConfiguration } = ConfigDictionary;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [scrollTop, setScrollTop] = useState<number>(0); 
  const scrollRef = useRef<HTMLDivElement>(null);
  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [dontAllowContainerScroll, setDontContainerScroll] = useState(true);
  const getFilteredRows = () => {
    if (!searchedValue.trim()) {
      return selectedSecondarySchemeRow?.invoices || [];
    }
  
    const searchTerm = searchedValue.toLowerCase().replace(/\s/g, '');
    return (selectedSecondarySchemeRow?.invoices || []).filter((row: any) => {
      return (
        row.invoiceNumber?.toLowerCase().replace(/\s/g, '').includes(searchTerm) ||
        row.outletName?.toLowerCase().replace(/\s/g, '').includes(searchTerm) ||
        row.invoiceDate?.toLowerCase().replace(/\s/g, '').includes(searchTerm)
      );
    });
  };

useEffect(() => {
  const filteredRows = getFilteredRows();
  setTableRows([...filteredRows]);
  initialFinalAmountsRef.current = filteredRows.reduce((acc: any, row: any) => {
    acc[row.id] = row.finalAmount;
    return acc;
  }, {} as Record<string, number>);
}, [selectedSecondarySchemeRow, month, searchedValue]);

  useEffect(() => {
    if (selectedSecondarySchemeRow) {
      const totalCases = tableRows.reduce((sum, row) => sum + row.cases, 0);
      const totalOthers = tableRows.reduce((sum, row) => sum + row.others, 0);
      const totalPieces = tableRows.reduce((sum, row) => sum + row.pieces, 0);
      const totalAmount = tableRows.reduce((sum, row) => sum + row.amount, 0);
      const totalDiscount = tableRows.reduce((sum, row) => sum + row.finalAmount, 0);
      const otherUnitName = selectedSecondarySchemeRow?.otherUnitName || otherUnitLabel;

      const totalQty = [
        caseUnitLabel && `${totalCases} ${caseUnitLabel}`,
        otherUnitName && `${totalOthers} ${otherUnitName}`,
        packUnitLabel && `${totalPieces} ${packUnitLabel}`
      ]
        .filter(Boolean)
        .join(", ");      
    
    const updatedClaimSummaryArr = {
      ...claimSummaryObject,
      ["SKU Name"]: selectedSecondarySchemeRow?.skuName,
      ["Claim Type"]: claimTypes[1],
      ["Selected Month"]: month,
      ["Invoices"]: tableRows.length.toString(),
      ["Total Qty"]: totalQty,
      ["Total Amount"]: `${truncateToTwoDecimals(totalAmount ?? 0)}`,
      ["Total Order Discount"]: `${truncateToTwoDecimals(totalDiscount ?? 0)}`,
    };
     
      setClaimSummaryObject((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(updatedClaimSummaryArr)) {
          return updatedClaimSummaryArr;
        }
        return prev;
      });
    }
  }, [tableRows, selectedSecondarySchemeRow]);

  const handleSaveChanges = () => {
    if (!selectedSecondarySchemeRow) return;
    
    const totalFoc = tableRows.reduce((sum, row) => sum + (row.foc || 0), 0);
    const totalFinalAmount = tableRows.reduce((sum, row) => sum + row.finalAmount, 0);
    const invoiceCount = tableRows.length;
    
    if (invoiceCount === 0) {
      openPopup("Alert", "At least one invoice must be selected.");
      return;
    }
    
    if (totalFinalAmount === 0) {
      openPopup("Alert", "Total final amount cannot be 0.");
      return;
    }
  
    const updatedSecondarySchemesRows = secondarySchemesRows.map((row: any) => {
      if (row.skuCode === selectedSecondarySchemeRow.skuCode && 
        row.schemeType === selectedSecondarySchemeRow.schemeType) {
        return {
          ...row,
          invoices: tableRows,
          cases: tableRows.reduce((sum, inv) => sum + inv.cases, 0),
          others: tableRows.reduce((sum, inv) => sum + inv.others, 0),
          pieces: tableRows.reduce((sum, inv) => sum + inv.pieces, 0),
          totalAmount: tableRows.reduce((sum, inv) => sum + inv.amount, 0),
          discount: totalFinalAmount,
          finalAmount: totalFinalAmount,
          totalFoc: totalFoc, 
          invoiceNo: tableRows.length
        };
      }
      return row;
    });
    dispatch(setSecondarySchemesRows(updatedSecondarySchemesRows));
    navigate(-1); 
  };

  const handleDeleteRow = (invoiceRow: any) => {
    setTableRows((prevRows) => {
      const newRows = prevRows.filter((row) => row.id !== invoiceRow.id);
      
      if (invoiceRow.foc === 1) {
        const updatedSecondarySchemesRows = secondarySchemesRows.map((row: any) => {
          if (row.skuCode === selectedSecondarySchemeRow?.skuCode && 
            row.schemeType === selectedSecondarySchemeRow?.schemeType) {
            return {
              ...row,
              totalFoc: Math.max(0, row.totalFoc - 1) 
            };
          }
          return row;
        });
        dispatch(setSecondarySchemesRows(updatedSecondarySchemesRows));
      }
      
      return newRows;
    });
  };

  const handleResetChanges = () => {
    if (!selectedSecondarySchemeRow) return;
    const initialRowData = initialSecondarySchemesRows.find(
      (row: any) => row.skuCode === selectedSecondarySchemeRow.skuCode &&
      row.schemeType === selectedSecondarySchemeRow.schemeType
    )?.invoices || [];
    
    setTableRows(initialRowData.map((row:any) => ({
      ...row,
      finalAmount: initialFinalAmountsRef.current[row.id] || row.finalAmount
    })));
  };

  const handleAccordionToggle = (isExpanded: boolean) => {
    setIsScrolling(!isExpanded);
    setIsAccordionExpanded(isExpanded);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  
    const handler = createScrollHandler(
      scrollRef,
      isScrolling,
      setAllowTableScroll,
      setDontContainerScroll
    );
  
    el.addEventListener("wheel", handler);
  
    return () => {
      el.removeEventListener("wheel", handler);
    };
  }, [isScrolling, dontAllowContainerScroll]);

  return (
    <div className="invoiceDetails" onWheel={(e) => handleScroll(e, setIsScrolling, scrollTop)}>
      <div className="top-heading">
        <FormHeader
          parentLabel={"Create New Claim "}
          label={"Invoice Details"}
          goBackToParent={() => navigate(-1)}
        />
        <div style={{ width: "280px" }}>
          <Search searchFunction={handleSearchValueChange} />
        </div>
      </div>

      <div ref={scrollRef} style={{
        gap: "14px",
        overflow: (dontAllowContainerScroll) ? "hidden" : "scroll",
        height: "100%",
        scrollbarWidth: "none", // Firefox
        msOverflowStyle: "none", // IE 10+
      }}>
        <CustomAccordian
          expanded={!isScrolling && isAccordionExpanded}
          onCustomAccordionToggle={handleAccordionToggle}
          metaData1={[
            { keys: "SKU Name", label: "SKU Name", index: 1 },
            { keys: "Claim Type", label: "Claim Type", index: 2 },
            { keys: "Selected Month", label: "Selected Month", index: 3 },
            { keys: "Invoices", label: "Invoices", index: 4 },
          ]}
          data1={claimSummaryObject}
          metaData2={[
            { keys: "Total Qty", label: "Total Qty", index: 1 },
            { keys: "Total Amount", label: "Total Amount", index: 2, currencySymbol: currency, valueType: "Number" },
            { keys: "Total Order Discount", label: "Total Order Discount", index: 3, currencySymbol: currency, valueType: "Number", valueStyle: { fontSize: "15px", fontWeight: 600 } },
          ]}
          metaData1ChunkSize={5}
          metaData2ChunkSize={5}
          data2={claimSummaryObject}
          header={`SKU Code ${selectedSecondarySchemeRow?.skuCode}`}
          label1=""
          isSecondHalf={true}
          singleColumnInEachHalf={true}
          status=""
        />
        <div style={{ height: "100%", position: "sticky", top: "0" }}>
          <div className="view_order_details_vo">
            <div className="invDetails-header">
              <Header title="Discounts & Promotions on SKU" />
              {secondarySchemesUIConfig.isEditable ?
                <Button
                  color="primary"
                  style={{
                    border: "#2b82a1 solid 1px",
                    backgroundColor: "#ffffff",
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "none",
                    marginTop: "8px",
                  }}
                  onClick={handleResetChanges}
                >
                  <div
                    style={{
                      height: "24px",
                      width: "19px",
                      paddingRight: "4px",
                    }}
                  >
                    <RenderSvg name={"backIcon"} />
                  </div>
                  Reset Changes
                </Button> :
                <></>
              }
            </div>

            <div className="invoice_claims_table_container">
              <CustomDataGrid
                allowTableScroll={allowTableScroll}
                isScrolling={isScrolling}
                setScrollTop={setScrollTop}
                columnsMetaData={getInvoicesColumns(batchIdConfiguration)}
                rows={tableRows}
                tableWidth={0.794}
                insideSecondarySchemes={true}
                {...(secondarySchemesUIConfig?.isEditable && { handleDeleteRow })}
                onSave={handleSaveChanges}
                enableRowClickRedirect={false}
              />
            </div>
          </div>
        </div>
      </div>
      <NewDiscountModal
        selectedSecondaryScheme={selectedSecondarySchemeRow}
        isClaimInvoiceView={true}
      />
    </div>
  );
};

export default InvoiceDetailsNew;